import { InputWrapperGridStyled } from '@theme'
import { Controller } from 'react-hook-form'
import { Button, Input, TextArea } from 'src/common'
import { CustomDragger } from 'src/common/upload/CustomDragger'
import { EMediaCategory, EMediaType } from 'src/interfaces/gallery'
import SelectMedia from '../../gallery/SelectMedia'
import { useDownload } from './useDownload'
import SelectMediaModal from 'src/components/gallery/SelectMediaModal'
import { Skeleton } from 'antd'
import { Fragment } from 'react'
import { t } from 'i18next'

interface IDownloadContentProps {
  activeTab?: string
}

export const DownloadContent = ({ activeTab }: IDownloadContentProps) => {
  const {
    control,
    setIsChange,
    onChangeDesktopRef,
    setImageSrc,
    setValue,
    setSelectedField,
    setShowModalSelectMedia,
    imageSrc,
    getValues,
    setIsModalLoading,
    isDisabledSaveButton,
    isModalLoading,
    onChangeMobileRef,
    imageMobileSrc,
    onSubmit,
    onSave,
    isChange,
    showModalSelectMedia,
    onCloseModalSelectMedia,
    onSelectMedia,
    getDownloadActionLoading,
  } = useDownload({
    activeTab,
  })

  return (
    <>
      {getDownloadActionLoading ? (
        <>
          <Skeleton
            paragraph={{ rows: 4 }}
            style={{
              marginTop: 10,
            }}
          />
        </>
      ) : (
        <Fragment>
          <InputWrapperGridStyled>
            <Controller
              name="title"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => {
                return (
                  <Input
                    alignment="col"
                    label="Title"
                    required
                    size="middle"
                    value={value}
                    placeholder={t('evolveLanding:enter_title')}
                    onChange={(e) => {
                      onChange(e?.target?.value)
                      setIsChange(true)
                    }}
                    errors={error?.message}
                  />
                )
              }}
            />

            <Controller
              name="titleInDutch"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => {
                return (
                  <Input
                    alignment="col"
                    label="Title in Netherlands"
                    required
                    size="middle"
                    placeholder={t('evolveLanding:enter_title_in_NL')}
                    value={value}
                    onChange={(e) => {
                      onChange(e?.target?.value)
                      setIsChange(true)
                    }}
                    errors={error?.message}
                  />
                )
              }}
            />

            <Controller
              name="content"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => {
                return (
                  <TextArea
                    label="Content"
                    name="content"
                    onChange={(e) => {
                      onChange(e?.target?.value)
                      setIsChange(true)
                    }}
                    value={value}
                    placeholder={t('evolveLanding:enter_content')}
                    alignment="col"
                    errors={error?.message}
                    containerClassName="mt-3"
                    rows={6}
                    disableResize={true}
                  />
                )
              }}
            />

            <Controller
              name="contentInDutch"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => {
                return (
                  <TextArea
                    label="Content in Netherlands"
                    name="contentInDutch"
                    onChange={(e) => {
                      onChange(e?.target?.value)
                      setIsChange(true)
                    }}
                    placeholder={t('evolveLanding:enter_content_in_NL')}
                    value={value}
                    alignment="col"
                    errors={error?.message}
                    containerClassName="mt-3"
                    rows={6}
                    disableResize={true}
                  />
                )
              }}
            />

            <div className="flex flex-col gap-1 items-end">
              <Controller
                name="mediaId"
                control={control}
                render={({ field: { onChange }, fieldState: { error } }) => {
                  onChangeDesktopRef.current = onChange
                  return (
                    <CustomDragger
                      label="Image"
                      name="mediaId"
                      id="mediaId"
                      containerClassName="mt-3"
                      alignment="col"
                      required
                      onLoadEnd={(data) => {
                        setValue('mediaId', data?.id ?? 0)
                        onChange(data?.id ?? 0)
                        setIsChange(true)
                        setImageSrc((prev) => data.original ?? prev)
                      }}
                      errors={error?.message}
                      initResource={imageSrc}
                      reset={getValues('mediaId') === 0}
                      limitFileSize={5}
                      allowFileTypes={['image/png', 'image/jpeg', 'image/jpg']}
                      changeLoading={setIsModalLoading}
                      uploadType={EMediaType.IMAGE as any}
                      uploadCategory={EMediaCategory.CONTENT}
                    />
                  )
                }}
              />
              <SelectMedia
                isNotGrid
                onClickSelectMedia={() => {
                  setSelectedField((pre) => 'desktop')
                  setShowModalSelectMedia(true)
                }}
                isLoadingMedia={isModalLoading}
              />
            </div>

            <div className="flex flex-col gap-1 items-end mb-4">
              <Controller
                name={'mediaMobileId'}
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => {
                  onChangeMobileRef.current = onChange
                  return (
                    <>
                      <CustomDragger
                        label="Image mobile"
                        containerClassName="mt-[10px]"
                        alignment="col"
                        onLoadEnd={(data) => {
                          setValue('mediaMobileId', data?.id ?? 0)
                          onChange(data?.id ?? 0)
                          setIsChange(true)
                        }}
                        name="mediaMobileId"
                        id="mediaMobileId"
                        required
                        errors={error?.message}
                        multiple={false}
                        initResource={imageMobileSrc}
                        reset={getValues('mediaMobileId') === 0}
                        allowFileTypes={[
                          'image/png',
                          'image/jpeg',
                          'image/jpg',
                        ]}
                        limitFileSize={5}
                        changeLoading={setIsModalLoading}
                        uploadType={EMediaType.IMAGE as any}
                        uploadCategory={EMediaCategory.CONTENT}
                      />
                    </>
                  )
                }}
              />
              <SelectMedia
                isNotGrid
                onClickSelectMedia={() => {
                  setSelectedField((pre) => 'mobile')
                  setShowModalSelectMedia(true)
                }}
                isLoadingMedia={isModalLoading}
              />
            </div>
          </InputWrapperGridStyled>
          <div className="landing-image-wrapper mt-6">
            <div className="landing-image-action mt-5">
              <div className="landing-save-cancel flex items-center justify-end">
                <Button
                  type="primary"
                  onClick={onSave}
                  loading={isDisabledSaveButton }
                  disabled={isDisabledSaveButton || isModalLoading || !isChange}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
          {showModalSelectMedia ? (
            <>
              <SelectMediaModal
                isOpen={showModalSelectMedia}
                onClose={onCloseModalSelectMedia}
                mediaType={EMediaType.IMAGE}
                category={EMediaCategory.CONTENT}
                onSelectMedia={onSelectMedia}
              />
            </>
          ) : (
            <></>
          )}
        </Fragment>
      )}
    </>
  )
}
